import { useQuery } from '@tanstack/react-query';
import { CalendarClockIcon, CalendarRangeIcon, ChevronDownIcon, DownloadIcon } from 'lucide-react';
import { useState } from 'react';
import { Link, useNavigate } from 'react-router';
import { ErrorState } from '@/components/ErrorState';
import { PageIntro } from '@/components/PageIntro';
import { JobStatusBadge } from '@/components/StatusBadge';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { RangeDialog } from '@/features/reports/RangeDialog';
import { fmtInZone } from '@/features/reports/schedule';
import { useMe } from '@/lib/auth';
import { downloadCsv } from '@/lib/csv';
import { fmtNumber, fmtPercent, fmtRelative } from '@/lib/format';
import { orgReportsOverview, payloadToCsv, type ReportSection, type ReportWindowKind } from '@/lib/reports';
import { useQueryState } from '@/lib/url';
import { cn } from '@/lib/utils';
import { DeliveriesChart, ListChangesChart, RankedBars, type RankedRow } from './overview/charts';
import { KpiBand } from './overview/KpiBand';
import { Card, CollapsibleTable, SectionCard, StatStrip } from './overview/SectionCard';
import { useUsage } from './queries';
import { UsageTabs } from './UsageReportsPage';

const WINDOWS = [7, 30, 90] as const;
const OVERVIEW_SECTIONS = ['messages', 'jobs', 'automation', 'keywords', 'contacts', 'scheduled', 'people', 'support'];

/**
 * Organization → Reports → Overview. The period's headline numbers against the period before, deliveries and
 * list changes by day, the jobs and keywords that drove them, then every catalog section as a card. All of it
 * comes from the same engine the scheduled reports use, so what you see here is what a digest would say.
 */
export function UsagePage() {
  const me = useMe();
  const navigate = useNavigate();
  const [params, setParams] = useQueryState();
  const from = params.get('from');
  const to = params.get('to');
  const custom = !!(from && to);
  const daysParam = params.get('days') ?? '30';
  const days = WINDOWS.includes(Number(daysParam) as (typeof WINDOWS)[number]) ? Number(daysParam) : 30;
  const setDays = (v: string | null) => setParams({ days: v, from: null, to: null }, { replace: true });
  const [picking, setPicking] = useState(false);
  const timezone = me.tenant.quietHours?.timezone || 'UTC';

  const overview = useQuery({ queryKey: ['org', 'reports', 'overview', custom ? `${from}|${to}` : days], queryFn: () => orgReportsOverview(custom ? { from: from as string, to: to as string } : { days }), staleTime: 60_000, placeholderData: (prev) => prev });
  const usage = useUsage();
  const data = overview.data;
  const stale = overview.isFetching && !!data;
  const section = (key: string): ReportSection | undefined => data?.payload.sections.find((s) => s.key === key);

  const periodLabel = data ? data.payload.window.label : `Last ${days} days`;
  const previousLabel = data ? `${fmtInZone(data.previous.from, timezone, { month: 'short', day: 'numeric' })} – ${fmtInZone(new Date(new Date(data.previous.to).getTime() - 1).toISOString(), timezone, { month: 'short', day: 'numeric' })}` : 'the previous period';

  // By job, from the catalog's own table (Job, Messages, Recipients, Delivered, Contacts added, Opt-outs).
  const jobsTable = section('jobs')?.table;
  const jobRows: RankedRow[] = (jobsTable?.rows ?? [])
    .map((r) => ({ key: String(r[0]), label: String(r[0]), value: Number(r[2] ?? 0), meta: String(r[3] ?? '—') === '—' ? undefined : `${String(r[3])} delivered`, hint: `${fmtNumber(Number(r[1] ?? 0))} ${Number(r[1]) === 1 ? 'message' : 'messages'} · ${fmtNumber(Number(r[4] ?? 0))} added · ${fmtNumber(Number(r[5] ?? 0))} opted out` }))
    .sort((a, b) => b.value - a.value);
  const keywordsSection = section('keywords');
  const keywordRows: RankedRow[] = (keywordsSection?.table?.rows ?? []).map((r) => ({ key: String(r[0]), label: <span className="font-mono text-xs tracking-wide">{String(r[0])}</span>, value: Number(r[1] ?? 0) }));

  const scheduleThis = () => {
    const windowKind: ReportWindowKind = custom ? 'days' : days === 7 ? 'week' : days === 30 ? 'month' : 'days';
    const template = {
      name: days === 7 && !custom ? 'Weekly overview' : 'Overview digest',
      triggerKind: 'schedule' as const,
      cron: days === 7 && !custom ? '0 8 * * 1' : '0 9 1 * *',
      windowKind,
      windowDays: windowKind === 'days' ? (custom && data ? data.days : days) : null,
      sections: OVERVIEW_SECTIONS,
      format: 'detailed' as const,
      channels: ['email', 'in_app'] as const,
      recipients: { roles: ['org_admin'] },
    };
    sessionStorage.setItem('crewline:report-draft', JSON.stringify(template));
    void navigate('/org/reports/scheduled?new=draft');
  };

  const rows = usage.data?.items ?? [];
  const totals = rows.reduce((acc, r) => ({ contacts: acc.contacts + r.contactCount, messages: acc.messages + r.messageCount, attempted: acc.attempted + r.sendsAttempted, delivered: acc.delivered + r.sendsDelivered }), { contacts: 0, messages: 0, attempted: 0, delivered: 0 });

  return (
    <div className="space-y-5 sm:space-y-6">
      <PageIntro title="Reports" description="How the organization is using Crewline: what went out, who it reached, what the automation did and who joined. Every number here can become a scheduled digest or an alert." />
      <UsageTabs />

      {/* ------------------------------------------------------------ period controls: one row, scopes everything below */}
      <div className="flex flex-col gap-3 md:flex-row md:items-end md:justify-between">
        <div className="min-w-0">
          <div className="flex flex-wrap items-center gap-2">
            <div role="radiogroup" aria-label="Period" className="inline-flex border">
              {WINDOWS.map((w) => (
                <button key={w} type="button" role="radio" aria-checked={!custom && days === w} onClick={() => setDays(w === 30 ? null : String(w))} className={cn('px-3 py-1.5 text-xs font-medium transition-colors', !custom && days === w ? 'bg-foreground text-background' : 'text-muted-foreground hover:bg-accent hover:text-foreground')}>
                  {w} days
                </button>
              ))}
              <button type="button" role="radio" aria-checked={custom} onClick={() => setPicking(true)} className={cn('flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium transition-colors', custom ? 'bg-foreground text-background' : 'text-muted-foreground hover:bg-accent hover:text-foreground')}>
                <CalendarRangeIcon className="size-3.5" aria-hidden /> {custom ? 'Custom' : 'Custom…'}
              </button>
            </div>
          </div>
          <p className="text-muted-foreground mt-2 text-xs">
            <span className="text-foreground font-medium">{periodLabel}</span> · {timezone}
            {data && <> · compared with {previousLabel}</>}
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            disabled={!data}
            onClick={() => {
              if (!data) return;
              const { filename, csv } = payloadToCsv(data.payload, `overview-${periodLabel}`);
              downloadCsv(filename, csv);
            }}
          >
            <DownloadIcon aria-hidden /> CSV
          </Button>
          <Button size="sm" onClick={scheduleThis}>
            <CalendarClockIcon aria-hidden /> Schedule this
          </Button>
        </div>
      </div>

      {overview.isError && !data ? (
        <ErrorState error={overview.error} onRetry={() => void overview.refetch()} />
      ) : (
        <div className={cn('space-y-6 transition-opacity', stale && 'opacity-60')} aria-busy={stale}>
          {/* ------------------------------------------------------------ headline numbers */}
          <KpiBand kpis={data?.kpis} series={data?.series} previousLabel={previousLabel} loading={!data} />

          {/* ------------------------------------------------------------ deliveries + by job */}
          <div className="grid gap-4 lg:grid-cols-3 lg:items-stretch">
            <Card title="Deliveries by day" meta="Every text attempted in the period, by outcome." className="lg:col-span-2" action={data ? <Totals items={[{ value: fmtNumber(data.kpis.current.attempted), label: 'attempted' }, { value: fmtNumber(data.kpis.current.delivered), label: 'delivered' }, { value: fmtNumber(data.kpis.current.failed), label: 'failed', bad: data.kpis.current.failed > 0 }]} /> : undefined}>
              {data ? <DeliveriesChart series={data.series} /> : <Skeleton className="h-64 w-full" />}
            </Card>
            <Card title="By job" meta="People reached, with each job's delivery rate.">
              {data ? (
                <div className="flex h-full flex-col">
                  <RankedBars rows={jobRows} unit="reached" emptyText="No active jobs sent anything in this period." />
                  {section('jobs')?.stats && (
                    <p className="text-muted-foreground mt-auto border-t pt-3 text-xs">
                      {section('jobs')!.stats!.map((s) => `${s.value} ${s.label.toLowerCase()}${s.hint ? ` · ${s.hint}` : ''}`).join(' · ')}
                    </p>
                  )}
                </div>
              ) : (
                <Skeleton className="h-40 w-full" />
              )}
            </Card>
          </div>

          {/* ------------------------------------------------------------ list changes + keywords */}
          <div className="grid gap-4 lg:grid-cols-3 lg:items-stretch">
            <Card title="List changes" meta="Contacts added above the line, opt-outs below it." className="lg:col-span-2" action={data ? <Totals items={[{ value: `+${fmtNumber(data.kpis.current.contactsAdded)}`, label: 'added' }, { value: `−${fmtNumber(data.kpis.current.optOuts)}`, label: 'opted out', bad: data.kpis.current.optOuts > 0 }, { value: `${data.kpis.current.contactsAdded - data.kpis.current.optOuts >= 0 ? '+' : '−'}${fmtNumber(Math.abs(data.kpis.current.contactsAdded - data.kpis.current.optOuts))}`, label: 'net' }]} /> : undefined}>
              {data ? <ListChangesChart series={data.series} /> : <Skeleton className="h-56 w-full" />}
            </Card>
            <Card title="Keywords & JOIN" meta="What workers texted in, and who signed themselves up.">
              {data ? (
                <div className="space-y-4">
                  <StatStrip stats={(keywordsSection?.stats ?? []).filter((s) => s.label !== 'Keyword replies')} />
                  <RankedBars rows={keywordRows} unit="replies" emptyText={keywordsSection?.empty ?? 'No keyword traffic in this period.'} />
                </div>
              ) : (
                <Skeleton className="h-40 w-full" />
              )}
            </Card>
          </div>

          {/* ------------------------------------------------------------ the rest of the catalog, as cards */}
          {data && (
            <>
              {section('messages') && <SectionCard section={{ ...section('messages')!, title: 'Messages in this period', stats: undefined, description: 'Every send, newest first.' }} initialRows={6} />}
              {section('automation') && <SectionCard section={section('automation')!} />}
              <div className="grid gap-4 2xl:grid-cols-2 2xl:items-start">
                {section('scheduled') && <SectionCard section={{ ...section('scheduled')!, title: 'Coming up', description: 'Scheduled messages and rules due in the next 7 days.' }} />}
                {section('support') && <SectionCard section={section('support')!} />}
              </div>
              {section('people') && <SectionCard section={section('people')!} />}
            </>
          )}

          {/* ------------------------------------------------------------ all time */}
          <details className="bg-card group border">
            <summary className="flex cursor-pointer list-none items-center justify-between gap-3 px-5 py-4 select-none">
              <span>
                <span className="text-sm font-semibold tracking-tight">All time, by job</span>
                <span className="text-muted-foreground ml-2 text-xs">
                  {usage.isPending ? 'Loading…' : `${fmtNumber(rows.length)} ${rows.length === 1 ? 'job' : 'jobs'} · ${fmtNumber(totals.contacts)} contacts · ${fmtNumber(totals.attempted)} texts attempted · ${totals.attempted > 0 ? fmtPercent((totals.delivered / totals.attempted) * 100) : '—'} delivered`}
                </span>
              </span>
              <ChevronDownIcon className="text-muted-foreground size-4 transition-transform group-open:rotate-180" aria-hidden />
            </summary>
            <div className="border-t">
              {usage.isError ? (
                <div className="p-5">
                  <ErrorState error={usage.error} onRetry={() => void usage.refetch()} />
                </div>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Job</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead className="text-right">Contacts</TableHead>
                      <TableHead className="text-right">Opted out</TableHead>
                      <TableHead className="text-right">Messages</TableHead>
                      <TableHead className="text-right">Texts attempted</TableHead>
                      <TableHead className="text-right">Delivered</TableHead>
                      <TableHead className="text-right">Delivery rate</TableHead>
                      <TableHead>Last message</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {rows.map((r) => (
                      <TableRow key={r.jobId}>
                        <TableCell>
                          <Link to={`/jobs/${r.jobId}`} className="font-medium hover:underline">
                            {r.jobName}
                          </Link>
                        </TableCell>
                        <TableCell>
                          <JobStatusBadge status={r.jobStatus} />
                        </TableCell>
                        <TableCell className="text-right tabular-nums">{fmtNumber(r.contactCount)}</TableCell>
                        <TableCell className="text-right tabular-nums">{fmtNumber(r.optedOutCount)}</TableCell>
                        <TableCell className="text-right tabular-nums">{fmtNumber(r.messageCount)}</TableCell>
                        <TableCell className="text-right tabular-nums">{fmtNumber(r.sendsAttempted)}</TableCell>
                        <TableCell className="text-right tabular-nums">{fmtNumber(r.sendsDelivered)}</TableCell>
                        <TableCell className="text-right tabular-nums">{fmtPercent(r.deliveryRate)}</TableCell>
                        <TableCell className="text-muted-foreground">{r.lastMessageAt ? fmtRelative(r.lastMessageAt) : '—'}</TableCell>
                      </TableRow>
                    ))}
                    {rows.length === 0 && !usage.isPending && (
                      <TableRow>
                        <TableCell colSpan={9} className="text-muted-foreground py-8 text-center">
                          No jobs yet.
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              )}
            </div>
          </details>

          <div className="bg-muted/40 flex flex-wrap items-center justify-between gap-3 border p-4 text-sm">
            <span className="text-muted-foreground">Want this in your inbox every Monday, or an alert when a send fails? Set it up once and choose who gets it.</span>
            <Button asChild size="sm" variant="outline">
              <Link to="/org/reports/scheduled">Scheduled reports & alerts</Link>
            </Button>
          </div>
        </div>
      )}

      {picking && (
        <RangeDialog
          open
          onClose={() => setPicking(false)}
          title="Overview for a period"
          description={`Boundaries are midnights in the organization’s time zone (${timezone}).`}
          confirmLabel="Show"
          timezone={timezone}
          onConfirm={(w) => {
            setParams({ from: w.windowStart, to: w.windowEnd, days: null }, { replace: true });
            setPicking(false);
          }}
        />
      )}
    </div>
  );
}

/** Figures in a chart card's header: the totals behind the plot, value first. */
function Totals({ items }: { items: Array<{ value: string; label: string; bad?: boolean }> }) {
  return (
    <dl className="hidden shrink-0 items-baseline gap-5 sm:flex">
      {items.map((i) => (
        <div key={i.label} className="text-right">
          <dd className={cn('text-lg leading-none font-semibold tracking-tight tabular-nums', i.bad && 'text-destructive')}>{i.value}</dd>
          <dt className="text-muted-foreground mt-0.5 text-[11px]">{i.label}</dt>
        </div>
      ))}
    </dl>
  );
}

// Keep the collapsible table export reachable for the dialogs' payload view.
export { CollapsibleTable };
